import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Package,
  Layers,
  ChefHat,
  Factory,
  Warehouse,
  Users,
  Truck,
  ShoppingCart,
  ShoppingBag,
  ScanLine,
  LogOut,
  UserCog,
  Settings as SettingsIcon,
  FileDown,
  FileText,
  Archive,
  Menu,
  BarChart3,
  Calendar as CalendarIcon,
  ClipboardList,
  Monitor,
  Cloud,
  Sparkles,
  Trophy,
  Sun,
  Moon,
  Container,
  Ban,
  Tag,
  ChevronDown,
  ChevronRight,
  Receipt,
  Boxes,
  ArrowDownToLine,
  Shield,
  MessageSquareWarning,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { hasPermission } from "../lib/permissions";

// Primary, daily-use areas — always visible at the top of the sidebar.
const NAV_PRIMARY = [
  { to: "/", label: "Start", icon: LayoutGrid, testid: "nav-sidebar-item-start" },
  { to: "/bestellungen", label: "Bestellungen", icon: ShoppingBag, testid: "nav-sidebar-item-bestellungen", module: "verkauf" },
  { to: "/produktion/board", label: "Aufträge & Wochenplan", icon: LayoutGrid, testid: "nav-production-board", module: "produktion" },
  { to: "/produktion", label: "Produktion", icon: Factory, testid: "nav-sidebar-item-produktion", module: "produktion" },
  { to: "/lieferscheine", label: "Lieferscheine", icon: Truck, testid: "nav-sidebar-item-lieferscheine", module: "verkauf" },
  { to: "/lager", label: "Lager & Bestand", icon: Package, testid: "nav-sidebar-item-lager", module: "lager" },
  { to: "/kunden", label: "Kunden", icon: Users, testid: "nav-sidebar-item-kunden", module: "crm" },
  { to: "/reklamationen", label: "Reklamationen", icon: MessageSquareWarning, testid: "nav-sidebar-item-reklamationen", module: "crm" },
  { to: "/b2b-preise", label: "B2B-Preise", icon: Tag, testid: "nav-sidebar-item-b2b-preise", module: "crm" },
  { to: "/sonderangebote", label: "Sonderangebote", icon: Tag, testid: "nav-sidebar-item-sonderangebote", module: "crm" },
  { to: "/rechnungen", label: "Rechnungen", icon: FileText, testid: "nav-sidebar-item-rechnungen", module: "rechnungen" },
  { to: "/rechnungsarchiv", label: "Rechnungsarchiv", icon: Archive, testid: "nav-sidebar-item-rechnungsarchiv", module: "rechnungen" },
];

// Secondary areas — grouped and collapsed by default. Everything stays reachable.
const NAV_GROUPS = [
  { key: "lagerprod", label: "Lager & Produktion", icon: Warehouse, items: [
    { to: "/lager?mode=zugang", label: "Wareneingang", icon: ArrowDownToLine, testid: "nav-goods-receipt", module: "lager" },
    { to: "/artikel", label: "Artikel", icon: Package, testid: "nav-items", module: "lager" },
    { to: "/chargen", label: "Chargen & MHD", icon: Layers, testid: "nav-batches", module: "lager" },
    { to: "/regale", label: "Regale", icon: Boxes, testid: "nav-shelf", module: "lager" },
    { to: "/regale/verwalten", label: "Regal-Verwaltung", icon: LayoutGrid, testid: "nav-shelves-manage", module: "lager" },
    { to: "/regale/rundgang", label: "Regal-Rundgang", icon: ScanLine, testid: "nav-shelves-tour", module: "lager" },
    { to: "/produktion/planung", label: "Produktion · Planung", icon: Factory, testid: "nav-production-planung", module: "produktion" },
    { to: "/produktion/bedarf", label: "Produktion · Bedarf", icon: ClipboardList, testid: "nav-production-demand", module: "produktion" },
    { to: "/produktion/ki", label: "KI-Optimierung", icon: Sparkles, testid: "nav-production-ki", module: "produktion" },
    { to: "/produktion/monitor", label: "Produktion · Monitor", icon: Factory, testid: "nav-production-monitor", module: "produktion" },
    { to: "/produktion/ausschuss", label: "Produktion · Ausschuss", icon: Ban, testid: "nav-production-ausschuss", module: "produktion" },
    { to: "/rezepturen", label: "Rezepturen", icon: ChefHat, testid: "nav-recipes", module: "rezepturen" },
    { to: "/einstellungen/etiketten", label: "Etiketten-Designer", icon: Tag, testid: "nav-label-designer", module: "labels" },
  ]},
  { key: "verkaufversand", label: "Verkauf & Versand", icon: Receipt, items: [
    { to: "/umsatz", label: "Umsatzbericht", icon: BarChart3, testid: "nav-revenue", module: "verkauf" },
    { to: "/top-kunden", label: "Top-Kunden", icon: Trophy, testid: "nav-top-customers", module: "verkauf" },
    { to: "/stornoprotokoll", label: "Stornoprotokoll", icon: Ban, testid: "nav-stornos", module: "rechnungen" },
    { to: "/versand", label: "Sendungen", icon: Truck, testid: "nav-shipping", module: "versand" },
    { to: "/paletten", label: "Palettenkonto", icon: Container, testid: "nav-pallets", module: "versand" },
    { to: "/monitor/produktion", label: "Monitor", icon: Monitor, testid: "nav-monitor", module: "produktion" },
  ]},
  { key: "einkauf", label: "Einkauf", icon: ShoppingCart, items: [
    { to: "/lieferanten", label: "Lieferanten", icon: Truck, testid: "nav-suppliers", module: "einkauf" },
    { to: "/einkauf", label: "Einkaufsbelege", icon: ShoppingCart, testid: "nav-purchase", module: "einkauf" },
    { to: "/belegscan", label: "Belegscan", icon: ScanLine, testid: "nav-belegscan", module: "einkauf" },
    { to: "/bestellvorschlaege", label: "Bestellvorschläge", icon: Sparkles, testid: "nav-reorder", module: "einkauf" },
  ]},
  { key: "werkzeuge", label: "Werkzeuge", icon: ScanLine, items: [
    { to: "/kalender", label: "Kalender", icon: CalendarIcon, testid: "nav-calendar" },
    { to: "/scanner", label: "Scanner", icon: ScanLine, testid: "nav-scanner" },
  ]},
  { key: "verwaltung", label: "Verwaltung", icon: SettingsIcon, adminOnly: true, items: [
    { to: "/einstellungen", label: "Einstellungen", icon: SettingsIcon, testid: "nav-settings", adminOnly: true, module: "einstellungen" },
    { to: "/integrationen", label: "Integrationen", icon: Cloud, testid: "nav-integrations", adminOnly: true, module: "einstellungen" },
    { to: "/benutzer", label: "Benutzer & Rechte", icon: UserCog, testid: "nav-users", adminOnly: true, module: "einstellungen" },
  ]},
];

// Mobile bottom navigation — the core areas + a "Mehr" sheet.
const BOTTOM_NAV = [
  { to: "/", label: "Start", icon: LayoutGrid, testid: "nav-bottom-item-start" },
  { to: "/bestellungen", label: "Bestellung", icon: ShoppingBag, testid: "nav-bottom-item-bestellungen", module: "verkauf" },
  { to: "/produktion", label: "Produktion", icon: Factory, testid: "nav-bottom-item-produktion", module: "produktion" },
  { to: "/lager", label: "Lager", icon: Package, testid: "nav-bottom-item-lager", module: "lager" },
];

function isVisible(item, user) {
  if (item.adminOnly && !(user?.is_superadmin || user?.role === "admin")) return false;
  if (item.roles && !item.roles.includes(user?.role)) return false;
  if (item.module && !hasPermission(user, item.module, "read")) return false;
  return true;
}

function NavItem({ n, onNav, indent = false }) {
  return (
    <NavLink
      to={n.to}
      end={n.to === "/"}
      data-testid={n.testid}
      onClick={onNav}
      className={({ isActive }) =>
        `relative flex items-center gap-3 ${indent ? "pl-9 pr-3" : "px-3"} py-2.5 rounded-xl text-sm transition-colors ${
          isActive
            ? "bg-[var(--pop-pink)]/15 text-[var(--pop-pink)] font-semibold"
            : "text-white/65 hover:bg-white/5 hover:text-white"
        }`
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !indent && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full bg-[var(--pop-pink)]" />
          )}
          <n.icon size={indent ? 15 : 18} strokeWidth={2} />
          <span className="truncate">{n.label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavGroup({ group, user, onNav, pathname, openMap, setOpenMap }) {
  const visibleItems = group.items.filter((it) => isVisible(it, user));
  if (!visibleItems.length) return null;
  const hasActive = visibleItems.some((it) => pathname === it.to.split("?")[0]);
  const isOpen = openMap[group.key] ?? hasActive;
  const Icon = group.icon;
  return (
    <div>
      <button
        data-testid={`nav-group-${group.key}`}
        onClick={() => setOpenMap({ ...openMap, [group.key]: !isOpen })}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-[0.7rem] font-bold uppercase tracking-wider transition-colors ${
          hasActive ? "text-[var(--pop-pink)]" : "text-white/45 hover:bg-white/5 hover:text-white/70"
        }`}
      >
        <Icon size={15} strokeWidth={2} />
        <span className="flex-1 text-left">{group.label}</span>
        {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
      </button>
      {isOpen && (
        <div className="mt-0.5 space-y-0.5 pb-1">
          {visibleItems.map((it) => <NavItem key={it.to} n={it} onNav={onNav} indent />)}
        </div>
      )}
    </div>
  );
}

function SidebarBody({ user, onNav, exportDatev, logout, navigate }) {
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const [openMap, setOpenMap] = useState({});
  return (
    <div className="flex flex-col h-full bg-[var(--sidebar)] text-white">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-3">
          <img src="/logos/logo-white.png" alt="POPCRN" className="h-8 w-auto" />
          <span className="overline text-white/50 leading-none">ERP · v1</span>
        </div>
      </div>
      <nav className="px-3 space-y-1 flex-1 overflow-y-auto">
        {NAV_PRIMARY.filter((n) => isVisible(n, user)).map((n) => (
          <NavItem key={n.to} n={n} onNav={onNav} />
        ))}
        <div className="my-3 border-t border-white/10" />
        {NAV_GROUPS.filter((g) => isVisible(g, user)).map((g) => (
          <NavGroup key={g.key} group={g} user={user} onNav={onNav}
                    pathname={pathname} openMap={openMap} setOpenMap={setOpenMap} />
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <button
          data-testid="btn-theme-toggle"
          onClick={toggle}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          title={theme === "dark" ? "Zu Light-Mode wechseln" : "Zu Dark-Mode wechseln"}
        >
          {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          {theme === "dark" ? "Light-Mode" : "Dark-Mode"}
        </button>
        <button
          data-testid="btn-datev-export"
          onClick={exportDatev}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/70 hover:bg-white/5 hover:text-white transition-colors"
        >
          <FileDown size={14} /> DATEV CSV Export
        </button>
        <div className="mt-3 flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5">
          <div className="h-8 w-8 rounded-full bg-[var(--pop-pink)] flex items-center justify-center text-white font-bold text-xs">
            {(user?.name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate flex items-center gap-1" data-testid="sidebar-user-name">
              {user?.name}
              {user?.is_superadmin && (
                <Shield size={12} className="text-[var(--pop-accent)]" data-testid="sidebar-superadmin-badge" />
              )}
            </div>
            <div className="overline text-white/50">
              {user?.is_superadmin ? "Superadmin" : user?.role}
            </div>
          </div>
          <button
            data-testid="btn-logout"
            onClick={async () => { await logout(); navigate("/login"); }}
            className="text-white/60 hover:text-[var(--pop-pink)] transition-colors"
            title="Abmelden"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function BottomNav({ user, onMore }) {
  const tabs = BOTTOM_NAV.filter((n) => isVisible(n, user));
  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 inset-x-0 z-40 h-16 bg-[var(--surface)]/95 backdrop-blur-lg border-t border-[var(--border)] flex items-stretch"
    >
      {tabs.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.to === "/"}
          data-testid={n.testid}
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-0.5 text-[0.62rem] font-semibold transition-colors ${
              isActive ? "text-[var(--pop-pink)]" : "text-[var(--text-muted)]"
            }`
          }
        >
          <n.icon size={22} strokeWidth={2} />
          <span>{n.label}</span>
        </NavLink>
      ))}
      <button
        data-testid="nav-bottom-item-mehr"
        onClick={onMore}
        className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[0.62rem] font-semibold text-[var(--text-muted)]"
      >
        <Menu size={22} strokeWidth={2} />
        <span>Mehr</span>
      </button>
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  const exportDatev = async () => {
    try {
      const res = await api.get("/exports/datev.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "datev-export.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DATEV Export heruntergeladen");
    } catch (_e) {
      toast.error("DATEV Export fehlgeschlagen");
    }
  };

  return (
    <div className="flex h-screen bg-[var(--bg)]">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col" data-testid="app-sidebar">
        <SidebarBody
          user={user} onNav={() => {}}
          exportDatev={exportDatev} logout={logout} navigate={nav}
        />
      </aside>

      {/* Mobile: slim top brand bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 h-14 flex items-center justify-between px-4
                      bg-[var(--sidebar)] text-white border-b border-white/10">
        <div className="flex items-center gap-2">
          <img src="/logos/logo-white.png" alt="POPCRN" className="h-6 w-auto" />
          <span className="overline text-white/40">ERP</span>
        </div>
      </div>

      {/* Mobile: bottom navigation + "Mehr" sheet (full nav) */}
      <BottomNav user={user} onMore={() => setMoreOpen(true)} />
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="right" className="p-0 w-80 max-w-[85vw] border-none bg-[var(--sidebar)]" data-testid="mobile-more-sheet">
          <VisuallyHidden>
            <SheetTitle>Alle Bereiche</SheetTitle>
            <SheetDescription>Vollständige Navigation der POPCRN ERP-Anwendung</SheetDescription>
          </VisuallyHidden>
          <SidebarBody
            user={user}
            onNav={() => setMoreOpen(false)}
            exportDatev={() => { exportDatev(); setMoreOpen(false); }}
            logout={logout} navigate={nav}
          />
        </SheetContent>
      </Sheet>

      <main className="flex-1 overflow-y-auto pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="mx-auto max-w-[1536px] px-4 md:px-10 py-6 md:py-8">{children}</div>
      </main>
    </div>
  );
}
